/**
 * DeliverableSearchPicker — Wave 2 Batch G combobox for picking a
 * deliverable across every project the user belongs to.
 *
 * Shape:
 *
 *   ┌──────────────────────────────────────────┐
 *   │ [MOTE-3 · Title · Project Name        ✕] │  selected pill
 *   └──────────────────────────────────────────┘
 *
 *   ┌──────────────────────────────────────────┐
 *   │  Search by title or shorthand…            │  search input (when open)
 *   └──────────────────────────────────────────┘
 *   ┌──────────────────────────────────────────┐
 *   │  Project Aeeiee                          │
 *   │   [MOTE]  MOTE-3   Fix login bug    Today│
 *   │   [MOTE]  MOTE-7   Tweak header     Done │
 *   │  Project Marketing                       │
 *   │   [MKT]   MKT-2    New homepage     Today│
 *   │  ─────────────────────────────────────── │
 *   │  Showing first 20 — refine your query    │  footer (only when nextCursor)
 *   └──────────────────────────────────────────┘
 *
 * Keyboard:
 *   - Open: Click the trigger, focus the input, or hit ArrowDown / Enter.
 *   - Up/Down: move highlight through the flat list (project headings are
 *     skipped — they're rendered, but not navigable).
 *   - Enter: select the highlighted row, close the popover.
 *   - Escape: close (no selection change).
 *
 * Selection model: the picker lives between an external `value`
 * (`SearchDeliverableResult | null`) and `onChange(next)`. Clearing is
 * `onChange(null)` via the X button on the pill.
 *
 * Caller is responsible for debouncing the search string. We expose a
 * `query` / `onQueryChange` prop pair so the parent can debounce + drive
 * the cache key.
 *
 * The combobox is built INLINE rather than extending `<Dropdown>` because
 * `<Dropdown>` is a single-select static-options primitive — wrapping it
 * for async search would be more code than rolling a focused one here.
 * The keyboard nav idioms match `<Dropdown>` (ArrowDown/Up, Enter, Escape,
 * Home/End) so users don't have to context-switch.
 */

import { type KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { DeliverableKind } from '@/api/types-bridge'
import { chipStyleFor } from '@/lib/project-color'
import { type SearchDeliverableResult, useSearchDeliverables } from './use-search-deliverables'

/* -------------------------------------------------------------------------- */
/*                              Public API                                    */
/* -------------------------------------------------------------------------- */

export interface DeliverableSearchPickerProps {
  /** Currently selected card (null = nothing selected). */
  value: SearchDeliverableResult | null
  /** Called when the user picks a row, or hits the clear (✕) on the pill. */
  onChange: (next: SearchDeliverableResult | null) => void
  /** Optional kind filter. */
  kind?: DeliverableKind
  /** Optional project filter (default: all caller's projects). */
  projectId?: string
  /**
   * Restrict to cards in columns where `allowsTimer = true`. Set when
   * the picker is opened from a timer-start surface.
   */
  allowsTimerOnly?: boolean
  /** Disabled entire control. */
  disabled?: boolean
  /** Optional label rendered above the trigger. */
  label?: string
  /** Validation error message shown below the trigger. */
  error?: string
  /** Optional id for the trigger button. */
  id?: string
  /** Placeholder when no card is selected. */
  placeholder?: string
  /** Wrapper class. */
  className?: string
}

/* -------------------------------------------------------------------------- */
/*                              Helpers                                       */
/* -------------------------------------------------------------------------- */

/** Format the "selected pill" body (`MOTE-3 · Title · Project Name`). */
function formatSelectedLabel(r: SearchDeliverableResult): string {
  const parts: string[] = []
  if (r.shorthand) parts.push(r.shorthand)
  parts.push(r.title)
  parts.push(r.projectName)
  return parts.join(' · ')
}

interface NavigableRow {
  type: 'header' | 'row'
  /** Only set on `row`. */
  result?: SearchDeliverableResult
  /** Only set on `header`. */
  groupLabel?: string
  groupCode?: string
}

/**
 * Group results by `projectId` while preserving the BE's order. Returns a
 * flat list interleaving `header` and `row` entries so the renderer can
 * walk one array; rows carry an `index` separately for keyboard nav.
 */
function groupResults(items: SearchDeliverableResult[]): NavigableRow[] {
  const out: NavigableRow[] = []
  let currentProjectId: string | null = null
  for (const item of items) {
    if (item.projectId !== currentProjectId) {
      out.push({ type: 'header', groupLabel: item.projectName, groupCode: item.projectCode })
      currentProjectId = item.projectId
    }
    out.push({ type: 'row', result: item })
  }
  return out
}

/* -------------------------------------------------------------------------- */
/*                              Component                                     */
/* -------------------------------------------------------------------------- */

export function DeliverableSearchPicker({
  value,
  onChange,
  kind,
  projectId,
  allowsTimerOnly,
  disabled = false,
  label,
  error,
  id,
  placeholder,
  className = '',
}: DeliverableSearchPickerProps) {
  const { t } = useTranslation('common')
  const resolvedPlaceholder = placeholder ?? t('deliverablePicker.placeholder')
  const generatedId = useId()
  const triggerId = id ?? `deliverable-search-${generatedId}`
  const listboxId = `${triggerId}-listbox`

  const [open, setOpen] = useState(false)
  const [rawQuery, setRawQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const listboxRef = useRef<HTMLDivElement | null>(null)

  // 300ms debounce — re-keys TanStack Query on the trailing edge so caches
  // stay coherent while the user is typing fast.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(rawQuery.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [rawQuery])

  const searchQuery = useSearchDeliverables(
    {
      q: debouncedQuery,
      kind,
      projectId,
      allowsTimerOnly,
      limit: 20,
    },
    // Fire on open regardless of query — empty `q` lets the BE return a
    // prepopulated list (recent / matching-kind). The user can then type
    // to narrow.
    { enabled: open },
  )

  const flatItems: SearchDeliverableResult[] = useMemo(() => {
    if (!searchQuery.data) return []
    const all: SearchDeliverableResult[] = []
    for (const page of searchQuery.data.pages) {
      for (const item of page.items) all.push(item)
    }
    return all
  }, [searchQuery.data])

  const navigable = useMemo(() => groupResults(flatItems), [flatItems])

  // Indexes of `row` entries within `navigable`, used for keyboard nav so
  // we skip `header` rows.
  const rowIndexes = useMemo(
    () =>
      navigable.reduce<number[]>((acc, n, i) => {
        if (n.type === 'row') acc.push(i)
        return acc
      }, []),
    [navigable],
  )

  // Reset active index when results change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setActiveIndex has stable identity (React's setState contract); listing it adds noise without changing behavior.
  useEffect(() => {
    setActiveIndex(0)
  }, [debouncedQuery, kind, projectId, allowsTimerOnly])

  const moveActive = useCallback(
    (direction: 1 | -1) => {
      if (rowIndexes.length === 0) return
      setActiveIndex((current) => {
        const next = (current + direction + rowIndexes.length) % rowIndexes.length
        return next
      })
    },
    [rowIndexes.length],
  )

  const commit = useCallback(
    (rowIndex: number) => {
      const itemIdx = rowIndexes[rowIndex]
      if (itemIdx === undefined) return
      const entry = navigable[itemIdx]
      if (!entry || entry.type !== 'row' || !entry.result) return
      onChange(entry.result)
      setOpen(false)
      setRawQuery('')
      setDebouncedQuery('')
      triggerRef.current?.focus()
    },
    [navigable, onChange, rowIndexes],
  )

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        moveActive(1)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        moveActive(-1)
      } else if (event.key === 'Enter') {
        event.preventDefault()
        commit(activeIndex)
      } else if (event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
        triggerRef.current?.focus()
      } else if (event.key === 'Home') {
        event.preventDefault()
        setActiveIndex(0)
      } else if (event.key === 'End') {
        event.preventDefault()
        if (rowIndexes.length > 0) setActiveIndex(rowIndexes.length - 1)
      } else if (event.key === 'Tab') {
        setOpen(false)
      }
    },
    [activeIndex, commit, moveActive, rowIndexes.length],
  )

  const handleTriggerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        setOpen(true)
      }
    },
    [disabled],
  )

  // Click-outside close.
  useEffect(() => {
    if (!open) return
    const handleMouseDown = (event: globalThis.MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open])

  // Move focus into the input when popover opens.
  useEffect(() => {
    if (open) {
      inputRef.current?.focus()
    }
  }, [open])

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onChange(null)
  }

  const triggerBase =
    'flex w-full items-center justify-between gap-2 rounded-md border bg-bg-elevated px-3 py-2 text-start text-sm shadow-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-bg-muted'
  const triggerState = error
    ? 'border-danger text-fg focus:border-danger focus:ring-danger/40'
    : 'border-border-strong text-fg focus:border-fg focus:ring-fg'

  const showFooter = Boolean(searchQuery.data?.pages[searchQuery.data.pages.length - 1]?.nextCursor)

  return (
    <div className={`flex flex-col gap-1 ${className}`.trim()}>
      {label && (
        <label htmlFor={triggerId} className="text-sm font-medium text-fg">
          {label}
        </label>
      )}
      <div ref={containerRef} className="relative">
        <button
          ref={triggerRef}
          type="button"
          id={triggerId}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          onClick={() => !disabled && setOpen((v) => !v)}
          onKeyDown={handleTriggerKeyDown}
          className={`${triggerBase} ${triggerState}`}
        >
          {value ? (
            <SelectedPill value={value} onClear={handleClear} disabled={disabled} />
          ) : (
            <span className="min-w-0 flex-1 truncate text-fg-subtle">{resolvedPlaceholder}</span>
          )}
          <span aria-hidden="true" className="ms-2 text-fg-subtle">
            {open ? '▲' : '▼'}
          </span>
        </button>

        {open && (
          // biome-ignore lint/a11y/useFocusableInteractive: WAI-ARIA combobox listbox manages focus via aria-activedescendant on the input above; the listbox itself is mouse-only.
          <div
            ref={listboxRef}
            id={listboxId}
            // biome-ignore lint/a11y/useSemanticElements: WAI-ARIA combobox pattern; native select can't render this UI.
            role="listbox"
            aria-label={t('deliverablePicker.listAriaLabel')}
            className="absolute start-0 end-0 z-40 mt-1 overflow-hidden rounded-md border border-border bg-bg-elevated shadow-lg"
          >
            <div className="border-b border-border p-2">
              <input
                ref={inputRef}
                type="text"
                value={rawQuery}
                onChange={(e) => setRawQuery(e.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder={resolvedPlaceholder}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={
                  rowIndexes[activeIndex] !== undefined
                    ? `${listboxId}-opt-${rowIndexes[activeIndex]}`
                    : undefined
                }
                className="block w-full rounded border border-border bg-bg-elevated px-3 py-1.5 text-sm text-fg focus:border-fg focus:outline-none focus:ring-1 focus:ring-fg"
              />
            </div>

            <div className="max-h-72 overflow-auto py-1">
              {debouncedQuery.length === 0 && (
                <div className="px-3 py-2 text-sm text-fg-subtle">
                  {t('deliverablePicker.typeToSearch')}
                </div>
              )}
              {debouncedQuery.length > 0 && searchQuery.isLoading && (
                <div className="px-3 py-2 text-sm text-fg-subtle">
                  {t('deliverablePicker.searching')}
                </div>
              )}
              {debouncedQuery.length > 0 && !searchQuery.isLoading && searchQuery.isError && (
                <div className="px-3 py-2 text-sm text-danger">
                  {t('deliverablePicker.loadError')}
                </div>
              )}
              {debouncedQuery.length > 0 &&
                !searchQuery.isLoading &&
                !searchQuery.isError &&
                navigable.length === 0 && (
                  <div className="px-3 py-2 text-sm text-fg-subtle">
                    {t('deliverablePicker.noMatches')}
                  </div>
                )}
              {navigable.map((entry, idx) => {
                if (entry.type === 'header') {
                  return (
                    <div
                      key={`h-${idx}-${entry.groupLabel}`}
                      className="flex items-center gap-2 px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-fg-muted"
                    >
                      <ProjectChip code={entry.groupCode ?? ''} />
                      <span>{entry.groupLabel}</span>
                    </div>
                  )
                }
                const result = entry.result
                if (!result) return null
                const rowIdxInRowIndexes = rowIndexes.indexOf(idx)
                const isActive = rowIdxInRowIndexes === activeIndex
                return (
                  // biome-ignore lint/a11y/useFocusableInteractive: focus stays in the input; selection moves via aria-activedescendant.
                  <div
                    key={`${result.kind}-${result.id}`}
                    id={`${listboxId}-opt-${idx}`}
                    // biome-ignore lint/a11y/useSemanticElements: ARIA option role on <div>; <option> only works inside <select>.
                    role="option"
                    aria-selected={value?.id === result.id}
                    onMouseEnter={() => setActiveIndex(rowIdxInRowIndexes)}
                    onMouseDown={(event) => {
                      event.preventDefault()
                      commit(rowIdxInRowIndexes)
                    }}
                    className={[
                      'flex items-center gap-2 px-3 py-2 text-sm cursor-pointer',
                      isActive ? 'bg-bg-muted' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <ProjectChip code={result.projectCode} />
                    {result.shorthand && (
                      <span className="font-mono text-xs text-fg-muted tabular-nums">
                        {result.shorthand}
                      </span>
                    )}
                    <span className="flex-1 truncate text-fg">{result.title}</span>
                    <StatusPill name={result.statusName} allowsTimer={result.allowsTimer} />
                  </div>
                )
              })}
            </div>

            {showFooter && (
              <div className="border-t border-border px-3 py-1.5 text-xs text-fg-muted">
                {t('deliverablePicker.footerLimitReached')}
              </div>
            )}
          </div>
        )}
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                              Subcomponents                                 */
/* -------------------------------------------------------------------------- */

function SelectedPill({
  value,
  onClear,
  disabled,
}: {
  value: SearchDeliverableResult
  onClear: (event: React.MouseEvent<HTMLButtonElement>) => void
  disabled: boolean
}) {
  const { t } = useTranslation('common')
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2" title={value.title}>
      <ProjectChip code={value.projectCode} />
      {value.shorthand && (
        <span className="shrink-0 font-mono text-xs text-fg-muted tabular-nums">
          {value.shorthand}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate text-fg">{value.title}</span>
      {value.statusName && (
        <span className="shrink-0">
          <StatusPill name={value.statusName} allowsTimer={value.allowsTimer} />
        </span>
      )}
      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t('deliverablePicker.clearSelection')}
        className="ms-1 shrink-0 rounded p-0.5 text-fg-subtle hover:bg-bg-muted hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
      >
        ✕
      </button>
    </span>
  )
}

function ProjectChip({ code }: { code: string }) {
  if (!code) return null
  const style = chipStyleFor(code)
  return (
    <span
      className="inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase leading-none tabular-nums"
      style={style}
    >
      {code}
    </span>
  )
}

function StatusPill({ name, allowsTimer }: { name: string; allowsTimer: boolean }) {
  // Subtle — green-ish when it permits a timer (Today / Review / Done),
  // grey otherwise.
  const tone = allowsTimer
    ? 'border-success/40 bg-success/10 text-success-ink'
    : 'bg-bg-muted text-fg-muted border-border'
  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${tone}`}
    >
      {name}
    </span>
  )
}
